/**
 * Interest list view — UI only; state from useInterestsListViewModel.
 */

import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { ConfirmDialog } from '@/features/common/components/ConfirmDialog'
import type { InterestSortField } from '../models/interests_ui_state'
import { useInterestsListViewModel } from '../viewmodels/use_interests_list_viewmodel'
import { InterestIcon } from '../widgets/interest_icon'

export function InterestsListPageView() {
  const navigate = useNavigate()
  const { state, isLoading, error, isDeleting, actions } = useInterestsListViewModel()

  const getSortIcon = (field: InterestSortField) => {
    if (state.sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />
    }
    return state.sortDirection === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-gray-700" />
    ) : (
      <ArrowDown className="w-3 h-3 text-gray-700" />
    )
  }

  if (isLoading) {
    return (
      <InterestsLoadingState title="Interest Management" />
    )
  }

  if (error && state.interests.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Interest Management</h1>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-red-600 font-medium mb-2">Failed to load interests</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    )
  }

  const {
    filteredInterests,
    paginatedInterests,
    deleteDialog,
    totalPages,
    startIndex,
    endIndex,
  } = state

  return (
    <div className="space-y-6">
      <InterestsListHeader navigate={navigate} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={state.entriesPerPage}
              onChange={e => actions.setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="search" className="text-sm text-gray-700">
              Search:
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="search"
                type="text"
                value={state.searchTerm}
                onChange={e => actions.setSearch(e.target.value)}
                placeholder="Search interests..."
                className="pl-8 pr-4 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('id')}
                >
                  <div className="flex items-center gap-1 w-full">#{getSortIcon('id')}</div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('name')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Interest Name
                    {getSortIcon('name')}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Image
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('status')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedInterests.length > 0 ? (
                paginatedInterests.map((interest, index) => (
                  <tr key={interest.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {state.sortField === 'id' ? interest.id : startIndex + index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {interest.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        <InterestIcon name={interest.name} />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <InterestStatusBadge status={interest.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/interest/${interest.id}/edit`)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => actions.openDeleteDialog(interest.id, interest.name)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    No interests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {filteredInterests.length > 0 ? startIndex + 1 : 0} to{' '}
            {Math.min(endIndex, filteredInterests.length)} of {filteredInterests.length} entries
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={actions.goToPreviousPage}
              disabled={state.currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => actions.changePage(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  state.currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                style={
                  state.currentPage === page
                    ? { backgroundColor: '#019b9a', borderColor: '#019b9a' }
                    : undefined
                }
              >
                {page}
              </button>
            ))}
            <button
              onClick={actions.goToNextPage}
              disabled={state.currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={actions.closeDeleteDialog}
        onConfirm={() => void actions.confirmDelete()}
        title="Delete Interest"
        message={`Are you sure you want to delete "${deleteDialog.interestName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  )
}

function InterestsLoadingState({ title }: { title: string }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">{title}</h1>
      </div>
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    </div>
  )
}

function InterestsListHeader({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-semibold text-gray-800">Interest Management</h1>
      <button
        onClick={() => navigate('/admin/interest/add')}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
        style={{ backgroundColor: '#019b9a' }}
      >
        <Plus className="w-4 h-4" />
        ADD INTEREST
      </button>
    </div>
  )
}

function InterestStatusBadge({ status }: { status: 'active' | 'inactive' }) {
  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
        status === 'active' ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-800'
      }`}
      style={
        status === 'active' ? { backgroundColor: '#019b9a', color: '#ffffff' } : undefined
      }
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}
